import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Cpu,
  Database,
  Layers,
  Loader2,
  Zap,
  FileCode,
  Lock,
  HardDrive,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Label,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface MSSQLMemoryClerksViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const PALETTE = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
  '#84cc16', // lime
  '#6366f1', // indigo
  '#e11d48', // rose
  '#a855f7', // violet
  '#0ea5e9', // sky
  '#d97706', // dark amber
  '#059669', // dark emerald
  '#7c3aed', // dark purple
  '#db2777', // dark pink
  '#4f46e5', // dark indigo
  '#2563eb', // dark blue
  '#64748b', // slate
];

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-16 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span
              className="text-gray-200 text-[10px] truncate max-w-[150px] font-mono"
              title={entry.value}
            >
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const MSSQLMemoryClerksView: React.FC<MSSQLMemoryClerksViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [memoryData, setMemoryData] = useState<any>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('buffer');

  // Legend Hover States
  const [hoveredAll, setHoveredAll] = useState<string | null>(null);
  const [hoveredBuffer, setHoveredBuffer] = useState<string | null>(null);
  const [hoveredCache, setHoveredCache] = useState<string | null>(null);
  const [hoveredSys, setHoveredSys] = useState<string | null>(null);
  const [hoveredObj, setHoveredObj] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60', 10);
    const params: any = {
      cluster_id: activeCluster,
      clusted_id: activeCluster,
      minutes,
    };
    if (clientId) {
      params.client_id = clientId;
    }

    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/mssql/memory_clerks', { params });
      setMemoryData(res.data?.data || res.data);
    } catch (err) {
      console.warn('MSSQLMemoryClerksView fetch error:', err);
      setMemoryData(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const labels: string[] = memoryData?.labels || [];
  const series: Record<string, number[]> = memoryData?.series || {};
  const groups: string[] = memoryData?.groups || Object.keys(series);

  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60', 10);
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const timeLabels = formatTimeLabels(labels);

  // Formatted data points for recharts
  const formattedData = useMemo(() => {
    if (!labels || labels.length === 0) return [];
    return labels.map((_, idx) => {
      const point: Record<string, any> = { time: timeLabels[idx] };
      groups.forEach((g) => {
        const val = series[g]?.[idx];
        point[g] = val !== undefined && val !== null ? Number(Number(val).toFixed(2)) : 0;
      });
      return point;
    });
  }, [labels, series, groups, timeLabels]);

  const hasData = (arr: any[]) => {
    if (!arr || arr.length === 0) return false;
    return arr.some((item) =>
      Object.keys(item).some(
        (k) => k !== 'time' && typeof item[k] === 'number' && item[k] > 0
      )
    );
  };

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.15;
  };

  // Groupings for categorized charts
  const bufferClerks = ['MEMORYCLERK_SQLBUFFERPOOL', 'MEMORYCLERK_SQLSTORENG', 'MEMORYCLERK_SOSNODE'];
  const cacheClerks = [
    'CACHESTORE_SQLCP',
    'CACHESTORE_PHDR',
    'MEMORYCLERK_SQLQUERYPLAN',
    'MEMORYCLERK_SQLOPTIMIZER',
    'MEMORYCLERK_SQLQERESERVATIONS',
  ];
  const sysClerks = [
    'MEMORYCLERK_SQLCLR',
    'MEMORYCLERK_SOSMEMMANAGER',
    'MEMORYCLERK_SQLGENERAL',
    'MEMORYCLERK_XE_BUFFER',
    'MEMORYCLERK_XTP',
    'MEMORYCLERK_SOSOS',
  ];
  const objClerks = [
    'OBJECTSTORE_LOCK_MANAGER',
    'USERSTORE_SCHEMAMGR',
    'USERSTORE_TOKENPERM',
    'OBJECTSTORE_SERVICE_BROKER',
    'MEMORYCLERK_SQLCONNECTIONPOOL',
    'MEMORYCLERK_SQLTRACE',
    'MEMORYCLERK_SQLLOGPOOL',
  ];

  const filteredClerkKeys = useMemo(() => {
    if (selectedCategory === 'buffer') return bufferClerks.filter((k) => groups.includes(k));
    if (selectedCategory === 'cache') return cacheClerks.filter((k) => groups.includes(k));
    if (selectedCategory === 'sys') return sysClerks.filter((k) => groups.includes(k));
    if (selectedCategory === 'obj') return objClerks.filter((k) => groups.includes(k));
    return bufferClerks.filter((k) => groups.includes(k));
  }, [selectedCategory, groups]);

  const summary = memoryData?.summary;

  const totalMB = useMemo(() => {
    if (summary?.total !== undefined && summary.total !== null) return Number(summary.total);
    if (summary?.latest) {
      return Object.values(summary.latest).reduce((acc: number, val: any) => acc + (Number(val) || 0), 0);
    }
    return groups.reduce((acc: number, g: string) => {
      const arr = series[g] || [];
      const lastVal = arr[arr.length - 1];
      return acc + (Number(lastVal) || 0);
    }, 0);
  }, [summary, groups, series]);

  const bufferPoolMB = useMemo(() => {
    if (summary?.categories?.buffer_pool_mb !== undefined && summary.categories.buffer_pool_mb !== null) {
      return Number(summary.categories.buffer_pool_mb);
    }
    const buf = summary?.latest?.['MEMORYCLERK_SQLBUFFERPOOL'] ?? series['MEMORYCLERK_SQLBUFFERPOOL']?.[series['MEMORYCLERK_SQLBUFFERPOOL']?.length - 1];
    return buf !== undefined ? Number(buf) : 0;
  }, [summary, series]);

  const planCacheMB = useMemo(() => {
    if (summary?.categories?.plan_cache_mb !== undefined && summary.categories.plan_cache_mb !== null) {
      return Number(summary.categories.plan_cache_mb);
    }
    const sqlcp = summary?.latest?.['CACHESTORE_SQLCP'] ?? series['CACHESTORE_SQLCP']?.[series['CACHESTORE_SQLCP']?.length - 1] ?? 0;
    const phdr = summary?.latest?.['CACHESTORE_PHDR'] ?? series['CACHESTORE_PHDR']?.[series['CACHESTORE_PHDR']?.length - 1] ?? 0;
    return Number(sqlcp) + Number(phdr);
  }, [summary, series]);

  const storageEngineMB = useMemo(() => {
    if (summary?.categories?.storage_engine_mb !== undefined && summary.categories.storage_engine_mb !== null) {
      return Number(summary.categories.storage_engine_mb);
    }
    const store = summary?.latest?.['MEMORYCLERK_SQLSTORENG'] ?? series['MEMORYCLERK_SQLSTORENG']?.[series['MEMORYCLERK_SQLSTORENG']?.length - 1];
    return store !== undefined ? Number(store) : 0;
  }, [summary, series]);

  const queryExecMB = useMemo(() => {
    if (summary?.categories?.query_execution_mb !== undefined && summary.categories.query_execution_mb !== null) {
      return Number(summary.categories.query_execution_mb);
    }
    const qe = summary?.latest?.['MEMORYCLERK_SQLQERESERVATIONS'] ?? series['MEMORYCLERK_SQLQERESERVATIONS']?.[series['MEMORYCLERK_SQLQERESERVATIONS']?.length - 1];
    return qe !== undefined ? Number(qe) : 0;
  }, [summary, series]);

  const trackedClerksCount = summary?.categories?.tracked_clerks ?? groups.length;

  const formatMB = (val: number | undefined | null) => {
    if (val === undefined || val === null || isNaN(val)) return '—';
    if (val >= 1024) {
      return `${(val / 1024).toFixed(2)} GB`;
    }
    return `${Number(val).toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[350px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-orange-500 text-white shadow-md">
            <Cpu className="w-4 h-4" />
          </div>
          <h2 className="text-lg font-bold text-white">Memory</h2>
        </div>

        {/* Category Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto custom-scroll pb-1 sm:pb-0">
          {[
            { id: 'buffer', label: 'Buffer & Storage' },
            { id: 'cache', label: 'Plan & Cache' },
            { id: 'sys', label: 'System & Engine' },
            { id: 'obj', label: 'Object & User Stores' },
          ].map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition-all duration-150 whitespace-nowrap cursor-pointer ${selectedCategory === cat.id
                ? 'bg-orange-600 text-white shadow-md'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white border border-gray-700'
                }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Top Summary KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
        {/* Total Clerk Memory */}
        <div className="bg-gradient-to-br from-orange-950/20 to-gray-900/80 border border-orange-500/30 rounded-xl p-3 shadow-md flex flex-col justify-between">
          <div className="flex items-center justify-between text-[11px] text-gray-400 font-semibold mb-1">
            <span>Total Clerk Memory</span>
            <Cpu className="w-3.5 h-3.5 text-orange-400" />
          </div>
          <div className="text-base font-bold font-mono text-orange-400">
            {formatMB(totalMB)}
          </div>
        </div>

        {/* Buffer Pool */}
        <div className="bg-gradient-to-br from-blue-950/20 to-gray-900/80 border border-blue-500/30 rounded-xl p-3 shadow-md flex flex-col justify-between">
          <div className="flex items-center justify-between text-[11px] text-gray-400 font-semibold mb-1">
            <span>Buffer Pool</span>
            <Database className="w-3.5 h-3.5 text-blue-400" />
          </div>
          <div className="text-base font-bold font-mono text-blue-400">
            {formatMB(bufferPoolMB)}
          </div>
        </div>

        {/* Plan Cache */}
        <div className="bg-gradient-to-br from-purple-950/20 to-gray-900/80 border border-purple-500/30 rounded-xl p-3 shadow-md flex flex-col justify-between">
          <div className="flex items-center justify-between text-[11px] text-gray-400 font-semibold mb-1">
            <span>Plan Cache</span>
            <FileCode className="w-3.5 h-3.5 text-purple-400" />
          </div>
          <div className="text-base font-bold font-mono text-purple-400">
            {formatMB(planCacheMB)}
          </div>
        </div>

        {/* Storage Engine */}
        <div className="bg-gradient-to-br from-emerald-950/20 to-gray-900/80 border border-emerald-500/30 rounded-xl p-3 shadow-md flex flex-col justify-between">
          <div className="flex items-center justify-between text-[11px] text-gray-400 font-semibold mb-1">
            <span>Storage Engine</span>
            <HardDrive className="w-3.5 h-3.5 text-emerald-400" />
          </div>
          <div className="text-base font-bold font-mono text-emerald-400">
            {formatMB(storageEngineMB)}
          </div>
        </div>

        {/* Query Execution */}
        <div className="bg-gradient-to-br from-amber-950/20 to-gray-900/80 border border-amber-500/30 rounded-xl p-3 shadow-md flex flex-col justify-between">
          <div className="flex items-center justify-between text-[11px] text-gray-400 font-semibold mb-1">
            <span>Query Execution</span>
            <Zap className="w-3.5 h-3.5 text-amber-400" />
          </div>
          <div className="text-base font-bold font-mono text-amber-400">
            {formatMB(queryExecMB)}
          </div>
        </div>

        {/* Tracked Clerks */}
        <div className="bg-gradient-to-br from-indigo-950/20 to-gray-900/80 border border-indigo-500/30 rounded-xl p-3 shadow-md flex flex-col justify-between">
          <div className="flex items-center justify-between text-[11px] text-gray-400 font-semibold mb-1">
            <span>Tracked Clerks</span>
            <Layers className="w-3.5 h-3.5 text-indigo-400" />
          </div>
          <div className="text-base font-bold font-mono text-indigo-300">
            {trackedClerksCount} <span className="text-xs font-normal text-gray-400 font-sans">clerks</span>
          </div>
        </div>
      </div>

      {/* Main Full-Width Chart: Filtered Clerks Allocation */}
      <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3 border-b border-gray-700/50 pb-2">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-orange-400" />
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">
              Memory Clerks Allocation (MB)
            </h4>
          </div>
          <span className="text-[11px] text-gray-400">
            Showing {filteredClerkKeys.length} of {groups.length} memory clerks
          </span>
        </div>

        <div className="h-64 w-full flex items-center justify-center">
          {hasData(formattedData) ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={formattedData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={5}>
                  <Label value="MB" angle={-90} position="insideLeft" offset={10} fill="#9ca3af" fontSize={10} />
                </YAxis>
                <Tooltip
                  wrapperStyle={{ zIndex: 1000 }}
                  contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                />
                <Legend
                  content={
                    <ScrollableLegend
                      hoveredSeries={hoveredAll}
                      onHoverSeries={setHoveredAll}
                    />
                  }
                />
                {filteredClerkKeys.map((key, idx) => {
                  const color = PALETTE[idx % PALETTE.length];
                  return (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={key}
                      stroke={color}
                      strokeOpacity={getOpacity(hoveredAll, key)}
                      strokeWidth={hoveredAll === key ? 2.5 : 1.5}
                      dot={false}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <span className="text-xs text-gray-500 italic">No memory clerk telemetry available</span>
          )}
        </div>
      </div>

      {/* Row 2: Categorized Deep-Dives (Buffer Pool vs Plan Cache) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        {/* Graph 2: Buffer Pool & Storage Engine */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <Database className="w-4 h-4 text-blue-400" />
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">
              Buffer Pool & Storage Engine (MB)
            </h4>
          </div>
          <div className="h-48 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <defs>
                    <linearGradient id="colorBuf" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="colorStore" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="colorNode" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={4}>
                    <Label value="MB" angle={-90} position="insideLeft" offset={10} fill="#9ca3af" fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredBuffer}
                        onHoverSeries={setHoveredBuffer}
                      />
                    }
                  />
                  {bufferClerks
                    .filter((k) => groups.includes(k))
                    .map((key, i) => {
                      const colors = ['#3b82f6', '#10b981', '#f59e0b'];
                      const gradIds = ['colorBuf', 'colorStore', 'colorNode'];
                      const color = colors[i % colors.length];
                      return (
                        <Area
                          key={key}
                          type="monotone"
                          dataKey={key}
                          name={key.replace('MEMORYCLERK_', '')}
                          stroke={color}
                          strokeOpacity={getOpacity(hoveredBuffer, key)}
                          fillOpacity={getOpacity(hoveredBuffer, key) * 0.3}
                          fill={`url(#${gradIds[i % gradIds.length]})`}
                          strokeWidth={hoveredBuffer === key ? 2.5 : 1.5}
                          dot={false}
                        />
                      );
                    })}
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No buffer pool telemetry available</span>
            )}
          </div>
        </div>

        {/* Graph 3: Plan & Query Cache Stores */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <FileCode className="w-4 h-4 text-purple-400" />
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">
              Plan Cache & Query Execution (MB)
            </h4>
          </div>
          <div className="h-48 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={formattedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={4}>
                    <Label value="MB" angle={-90} position="insideLeft" offset={10} fill="#9ca3af" fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredCache}
                        onHoverSeries={setHoveredCache}
                      />
                    }
                  />
                  {cacheClerks
                    .filter((k) => groups.includes(k))
                    .map((key, i) => {
                      const colors = ['#a855f7', '#ec4899', '#3b82f6', '#06b6d4', '#f59e0b'];
                      const color = colors[i % colors.length];
                      return (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          name={key.replace('CACHESTORE_', '').replace('MEMORYCLERK_', '')}
                          stroke={color}
                          strokeOpacity={getOpacity(hoveredCache, key)}
                          strokeWidth={hoveredCache === key ? 2.5 : 1.5}
                          dot={false}
                        />
                      );
                    })}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No plan cache telemetry available</span>
            )}
          </div>
        </div>
      </div>

      {/* Row 3: System Engine Clerks & Object Stores */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        {/* Graph 4: System, OS & CLR Memory */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <Zap className="w-4 h-4 text-emerald-400" />
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">
              System, Engine & CLR Memory (MB)
            </h4>
          </div>
          <div className="h-48 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={formattedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={4}>
                    <Label value="MB" angle={-90} position="insideLeft" offset={10} fill="#9ca3af" fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredSys}
                        onHoverSeries={setHoveredSys}
                      />
                    }
                  />
                  {sysClerks
                    .filter((k) => groups.includes(k))
                    .map((key, i) => {
                      const colors = ['#10b981', '#14b8a6', '#06b6d4', '#8b5cf6', '#f97316', '#64748b'];
                      const color = colors[i % colors.length];
                      return (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          name={key.replace('MEMORYCLERK_', '')}
                          stroke={color}
                          strokeOpacity={getOpacity(hoveredSys, key)}
                          strokeWidth={hoveredSys === key ? 2.5 : 1.5}
                          dot={false}
                        />
                      );
                    })}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No system clerk telemetry available</span>
            )}
          </div>
        </div>

        {/* Graph 5: Object & User Stores */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <Lock className="w-4 h-4 text-cyan-400" />
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">
              Object & User Stores (MB)
            </h4>
          </div>
          <div className="h-48 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={formattedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={4}>
                    <Label value="MB" angle={-90} position="insideLeft" offset={10} fill="#9ca3af" fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredObj}
                        onHoverSeries={setHoveredObj}
                      />
                    }
                  />
                  {objClerks
                    .filter((k) => groups.includes(k))
                    .map((key, i) => {
                      const colors = ['#06b6d4', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#10b981', '#ef4444'];
                      const color = colors[i % colors.length];
                      return (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          name={key.replace('OBJECTSTORE_', '').replace('USERSTORE_', '').replace('MEMORYCLERK_', '')}
                          stroke={color}
                          strokeOpacity={getOpacity(hoveredObj, key)}
                          strokeWidth={hoveredObj === key ? 2.5 : 1.5}
                          dot={false}
                        />
                      );
                    })}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No object store telemetry available</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MSSQLMemoryClerksView;

